import { motion } from "framer-motion";
import CharlieHero from "../components/CharlieHero";
import Marquee from "../components/Marquee";
import FeelingTool from "../components/FeelingTool";
import PeekInsideBook from "../components/PeekInsideBook";
import LaunchNote from "../components/LaunchNote";
import WaitlistForm from "../components/WaitlistForm";
import { Link } from "react-router-dom";

const CHAPTERS = [
  {
    n: "01",
    title: "Written for the child's interior",
    body: "The best picture books do more than describe what happened. They give language to how it lands in a small body — the tight tummy, the fork that stops moving, the legs that itch in the car. That interior noticing is where Charlie begins.",
  },
  {
    n: "02",
    title: "Kind language for hard truths",
    body: "'Some people love well. Others love differently.' One sentence a five-year-old can hold, and a grown-up can lean on for years. We choose accuracy over comfort — softly, and without pretending the hard thing isn't there.",
  },
  {
    n: "03",
    title: "Tools you can actually use",
    body: "A thank-you rock in the pocket. A trusted-grown-ups list on the fridge. A name for the uh-oh feeling. Small, portable ideas your family, classroom, or session can start using this week — and keep using for years.",
  },
  {
    n: "04",
    title: "A series, not a single answer",
    body: "Four books, one steady voice — from parental absence to gratitude, from discernment to a child's own inner strength. A gentle library that grows with a child, and with the grown-ups reading alongside.",
  },
];

const fadeUp = { hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.65,0,0.35,1] } } };

export default function Home() {
  return (
    <>
      <CharlieHero />

      <Marquee items={[
        "For the parent holding steady",
        "For the counselor's bookshelf",
        "For the teacher's read-aloud circle",
        "For the child learning to name it",
        "For the tender, hard chapters",
      ]} />

      {/* Manifesto */}
      <section data-testid="manifesto-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
            <div className="md:col-span-8">
              <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">Four commitments</div>
              <h2 className="serif text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.02] tracking-tight text-espresso">
                What we're actually <span className="italic text-[#4A7C7B]">making.</span>
              </h2>
            </div>
            <div className="md:col-span-4 text-charcoal/90 text-sm md:text-base leading-relaxed">
              A short list of promises to the families, classrooms, and offices this series is written for.
            </div>
          </div>

          <ol className="grid md:grid-cols-2 gap-x-14 gap-y-16 md:gap-y-24">
            {CHAPTERS.map((c) => (
              <motion.li
                key={c.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start"
              >
                <div className="chapter-number text-[6rem] md:text-[9rem] leading-none -mt-4 md:-mt-6">{c.n}</div>
                <div>
                  <h3 className="serif text-2xl md:text-3xl leading-[1.15] text-espresso">{c.title}</h3>
                  <p className="mt-4 text-charcoal/90 leading-relaxed max-w-md">{c.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <FeelingTool />

      <PeekInsideBook />

      <LaunchNote />

      {/* Inline waitlist */}
      <section data-testid="home-waitlist" className="py-24 md:py-32 bg-[#F0EBE1]/60 border-y border-black/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">Join the waitlist</div>
            <h2 className="serif text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight text-espresso">
              A quiet note when <span className="italic">Book One</span> is ready.
            </h2>
            <p className="mt-6 text-charcoal/90 leading-relaxed max-w-md">
              One careful email at launch. In between, no more than one or two short notes a month.
            </p>
            <p className="mt-4 text-charcoal/85 leading-relaxed max-w-md">
              You'll also receive Charlie's welcome, the free educator's and clinician's guides ahead of launch, and one careful note the day Book One arrives.
            </p>
            <Link to="/waitlist" className="link-draw mt-6 inline-block text-sm">See the full form →</Link>
          </div>
          <div className="md:col-span-6">
            <WaitlistForm compact source="home" />
          </div>
        </div>
      </section>
    </>
  );
}
